<template>
  <mq-responsive target="desktop">
    <div v-if="isOpen" class="mobAppQRModal">
      <div class="mobAppQRModal__content">
        <div class="mobAppQRModal__close" @click="closeModal">
          <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M25.0967 25.0953L8.90625 8.90479M25.0967 8.90479L8.90625 25.0953" stroke="#5D5667" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="mobAppQRModal__title">
          {{ $t('mobAppQRModal.title') }}
        </div>
        <p class="mobAppQRModal__description">
          {{ $t('mobAppQRModal.description') }}
        </p>
        <div class="mobAppQRModal__qr">
          <img src="../../assets/images/mobAppQr/anyclass_qr.png" alt="logo"/>
        </div>
        <div class="mobAppQRModal__instruction">
          {{ $t('mobAppQRModal.instruction') }}
        </div>
      </div>
    </div>
  </mq-responsive>
</template>

<script lang="ts">
import { MqResponsive } from 'vue3-mq'
import { defineComponent, ref, onMounted, watch } from 'vue'
import { useStore } from 'vuex'

export default defineComponent({
  components: {
    MqResponsive
  },
  setup () {
    const store = useStore()
    const STORAGE_KEY = 'mobAppQRModal'
    const CLOSES_KEY = 'mobAppQRModalCloses'
    const DAY = 24 * 60 * 60 * 1000

    const isOpen = ref(false)
    const closes = ref(0)

    function getStorage () {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return null
      try {
        return JSON.parse(raw)
      } catch {
        return null
      }
    }

    function setStorage (date: number) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ date }))
    }

    function getCloses () {
      return parseInt(localStorage.getItem(CLOSES_KEY) || '0', 10)
    }

    function setCloses (val: number) {
      localStorage.setItem(CLOSES_KEY, val.toString())
    }

    function canShow () {
      const data = getStorage()
      const now = Date.now()
      const closesCount = getCloses()
      closes.value = closesCount

      if (!data) return true

      const last = data.date
      if (closesCount >= 2) {
        return now - last > 3 * DAY
      }
      return now - last > DAY
    }

    function show () {
      isOpen.value = true
    }

    function hide () {
      isOpen.value = false
    }

    function closeModal () {
      hide()
      setStorage(Date.now())
      if (closes.value < 2) {
        setCloses(closes.value + 1)
        return
      }
      setCloses(0)
    }

    function openModal () {
      isOpen.value = true
    }

    function checkUserAuth () {
      const isAuthenticated = store.getters['user/isLogged']
      const user = store.getters['user/user']
      const hasUser = Object.keys(user).length > 1
      return isAuthenticated && hasUser
    }

    function showAfterLogin () {
      if (checkUserAuth() && canShow()) {
        setTimeout(() => {
          show()
        }, 2000)
      }
    }

    watch(
      () => store.getters['user/isLogged'],
      (isAuthenticated) => {
        if (isAuthenticated) {
          showAfterLogin()
        }
      },
      { immediate: true }
    )

    onMounted(() => {
      showAfterLogin()
    })

    return {
      isOpen,
      closeModal,
      openModal,
      canShow,
      show,
      hide
    }
  }
})
</script>

<style lang="scss" scoped>
.mobAppQRModal {
  font-family: 'Inter', sans-serif;
  position: fixed;
  right: 0;
  bottom: 0;
  z-index: 90;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding-right: 20px;
  padding-bottom: 15px;
  @media (max-height: 730px) {
    transform: scale(0.75);
    transform-origin: bottom right;
  }

  &__content {
    position: relative;
    width: 431px;
    height: 664px;
    border-radius: 22px;
    padding: 52px 0px 42px 0px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    background-image: url('../../assets/images/mobAppQr/modalBackground.png');
    background-position: center;
    background-repeat: no-repeat;
  }

  &__close {
    position: absolute;
    top: 20px;
    right: 18px;
    cursor: pointer;
  }

  &__title {
    font-size: 38px;
    font-weight: 700;
    margin-bottom: 24px;
    padding: 0 37px;
  }

  &__description {
    font-size: 18px;
    font-weight: 500;
    margin-bottom: 36px;
    padding: 0 37px;
  }

  &__instruction {
    font-size: 16px;
    padding: 0 37px;
    color: #575765;
  }

  &__qr {
    width: 246px;
    height: 246px;
    margin-bottom: 32px;
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
}
</style>
